import { upsertLocally } from '@paragon/client-tools';
import { BearingEnvelope, PlaneCut, RoofPlane } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { isOverhangSignal, RoofPlaneEditInstructions } from '@paragon/layout';
import { enqueueError } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';
import { forceArray, notNullish } from '@paragon/utilities';

import { scrollTo } from '../../../scrolling';
import { getActiveLayoutLevelGuidOrNull, getAllRoofPlanesDictionary } from '../../selectors/layoutDataSelectors';
import { getPickingObjectInfo } from '../../selectors/pickingSelectors';
import {
  applicationOnInsertingRoofPlane,
  applicationOnPickingPlane,
  clearPickingObject,
  isInsertionRoofPlaneFullyDefined,
} from '../../slices/applicationSlice';
import { layoutSlice } from '../../slices/layoutSlice';
import { setMatchFasciaHeightsPanelOpen } from '../../slices/panelDataSlice';
import { ensureLevelIsVisible } from '../levelVisibilityActions';
import { selectItem } from '../selectActions';
import { AsyncThunkActionReturn, AsyncThunkActionVoid, ThunkActionVoid } from '../thunkActions';

export const startInsertingRoofPlane =
  (options?: { levelGuid?: string | null }): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();
    const levelGuid = options?.levelGuid ?? getActiveLayoutLevelGuidOrNull(state) ?? null;

    if (levelGuid) dispatch(ensureLevelIsVisible(levelGuid, { exclusive: true }));

    dispatch(
      applicationOnInsertingRoofPlane({ ...state.layout.roofPlaneInsertionSettings.defaultProperties, levelGuid }),
    );
  };

export const finishInsertingRoofPlane =
  (options?: { postInsertionAction?: 'repeat' }): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const { insertingObjectInfo } = getState().application;
    if (insertingObjectInfo?.type !== 'roofPlane' || !isInsertionRoofPlaneFullyDefined(insertingObjectInfo.data))
      return;

    const roofPlane = insertingObjectInfo.data;
    await dispatch(insertRoofPlane(roofPlane, options));

    if (options?.postInsertionAction !== 'repeat') {
      await dispatch(autoCutRecentlyInsertedPlanes());
    }
  };

export const insertRoofPlaneOnBearingEnvelope =
  (bearingEnvelope: BearingEnvelope): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const roofPlane: RoofPlane = {
      guid: crypto.randomUUID(),
      ...getState().layout.roofPlaneInsertionSettings.defaultProperties,
      flipped: false,
      geometry: { type: 'BearingEnvelope' as const, bearingEnvelopeGuid: bearingEnvelope.guid },
      levelGuid: bearingEnvelope.levelGuid,
      cuts: [],
    };
    await dispatch(insertRoofPlane(roofPlane, { postInsertionAction: 'nothing', extendsPreviousUndo: true }));
  };

const insertRoofPlane =
  (
    roofPlane: RoofPlane,
    options?: { postInsertionAction?: 'repeat' | 'nothing'; extendsPreviousUndo?: boolean },
  ): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const { roofPlaneInsertionSettings } = getState().layout;
    const postInsertionAction = options?.postInsertionAction ?? 'finish';
    dispatch(
      layoutSlice.actions.roofPlaneCreated({
        roofPlane,
        postInsertionAction,
        roofPlaneInsertionSettings,
        extendsPreviousUndo: options?.extendsPreviousUndo,
      }),
    );

    if (postInsertionAction === 'finish') {
      dispatch(selectItem(roofPlane, 'roofPlane'));
    }

    const success = await dispatch(saveRoofPlanes());
    if (success) {
      dispatch(layoutSlice.actions.recentRoofPlaneInserted(roofPlane.guid));
    }
  };

export const beginCuttingRoofPlane =
  (guid: string): AsyncThunkActionVoid =>
  async (dispatch) => {
    await dispatch(clearRoofPlaneCuts(guid));
    dispatch(applicationOnPickingPlane({ type: 'roof-cut', planeGuidToCut: guid }));
  };

export const handleRoofPlaneCutPicked =
  (cut: PlaneCut): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();
    const pickingObjectInfo = getPickingObjectInfo(state);

    if (pickingObjectInfo?.type !== 'pickingPlane' || pickingObjectInfo.targetInfo.type !== 'roof-cut') {
      throw new Error('Invalid picking state.');
    }

    const roofPlaneToCut = state.layout.roofPlanes[pickingObjectInfo.targetInfo.planeGuidToCut];
    if (roofPlaneToCut == null) throw new Error('Unable to find roof plane to cut.');

    await dispatch(
      editRoofPlanes({ guid: roofPlaneToCut.guid, editedProperties: { cuts: roofPlaneToCut.cuts.concat(cut) } }),
    );

    // FIXME: it would be awesome to have a heuristic to exit picking mode automatically once a plane is fully
    //   bounded by cuts. So far we haven't found a good one.

    runPostCutCreationHooks();
  };

export function runPostCutCreationHooks() {
  scrollTo('cutPlaneButton'); // adding cuts could push the "finish cutting" button off the screen
}

export const finishCuttingRoofPlane = () => clearPickingObject();

export const editRoofPlaneCutOverhang =
  (roofPlaneGuid: string, cutIndex: number, overhang: boolean): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const roofPlane = getState().layout.roofPlanes[roofPlaneGuid];
    const { cuts } = roofPlane;

    // Find this cut and the next non-overhang-signalling cut, to inform splicing to make a new cut array
    const realCutsWithIndices = cuts
      .map((cut, index) => (isOverhangSignal(cut, roofPlaneGuid) ? null : { cut, index }))
      .filter(notNullish);
    const cutIndices = realCutsWithIndices
      .map((cut, i) => {
        if (i !== cutIndex) return null;
        const nextRealCut = realCutsWithIndices[i + 1] as (typeof realCutsWithIndices)[0] | undefined;
        return { thisRealCutIndex: cut.index, nextRealCutIndex: nextRealCut?.index };
      })
      .filter(notNullish);

    if (!cutIndices.length) {
      console.error('Attempted to edit overhang on a non-real plane cut');
      return;
    }
    const { thisRealCutIndex, nextRealCutIndex } = cutIndices[0];

    const preceedingCuts = cuts.slice(0, thisRealCutIndex + 1);
    const newOverhangCuts: PlaneCut[] = overhang ? [{ type: 'AgainstPlane', cuttingPlaneGuid: roofPlaneGuid }] : [];
    const succeedingCuts = typeof nextRealCutIndex === 'number' ? cuts.slice(nextRealCutIndex) : [];
    const newCuts = [...preceedingCuts, ...newOverhangCuts, ...succeedingCuts];

    await dispatch(editRoofPlanes({ guid: roofPlaneGuid, editedProperties: { cuts: newCuts } }));
  };

export const autoCutPlanes =
  (roofPlaneGuids: readonly string[]): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const spinnerId = dispatch(addSpinnerMessage('Auto-cutting planes...'));

    const { application } = getState();
    if (application.openContext?.type !== 'project') throw new Error('Cannot edit planes outside of project');
    const { projectGuid } = application.openContext;

    try {
      const { roofPlanes } = await designService.projects.autoCutPlanes(projectGuid, roofPlaneGuids);

      // A cut that changes nothing returns no planes, and an empty upsert has no levels to record for undo.
      if (roofPlanes.length > 0) dispatch(layoutSlice.actions.roofPlanesUpserted(roofPlanes));
    } catch (error) {
      console.error(error);
      dispatch(enqueueError(ERROR_SAVING_MESSAGE));
      return;
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const autoCutRecentlyInsertedPlanes = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const state = getState();
  const recentRoofPlanes = state.layout.recentRoofPlaneInsertions;
  dispatch(layoutSlice.actions.recentRoofPlanesCleared());
  if (state.layout.roofPlaneInsertionSettings.autoCutOnInsert && recentRoofPlanes.length)
    await dispatch(autoCutPlanes(recentRoofPlanes));
};

const ERROR_SAVING_MESSAGE = 'Unable to save roof plane. Please try again or contact Paragon Customer Support.';

export const editRoofPlanes =
  (
    instructions: RoofPlaneEditInstructions | RoofPlaneEditInstructions[],
    options?: { createdPlanes?: RoofPlane[] },
  ): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();

    let existingPlanes = getAllRoofPlanesDictionary(state);
    if (options?.createdPlanes) {
      existingPlanes = upsertLocally(existingPlanes, options.createdPlanes);
    }

    const editedPlanes = forceArray(instructions).map(({ guid, editedProperties }) => {
      const roofPlane = existingPlanes[guid];
      if (roofPlane == null) {
        dispatch(enqueueError(ERROR_SAVING_MESSAGE));
        throw new Error(`Unable to find roof plane '${guid}' in state to edit.`);
      }

      return { ...roofPlane, ...editedProperties };
    });

    dispatch(layoutSlice.actions.roofPlanesUpserted(editedPlanes));

    if (state.application.openContext?.type === 'project') {
      await dispatch(saveRoofPlanes());
    }
  };

export const clearRoofPlaneCuts = (guid: string) => editRoofPlanes({ guid, editedProperties: { cuts: [] } });

/** Returned boolean indicates successful save */
export const saveRoofPlanes = (): AsyncThunkActionReturn<boolean> => async (dispatch, getState) => {
  const state = getState();
  if (state.application.openContext?.type !== 'project') return false;

  const spinnerId = dispatch(addSpinnerMessage('Saving roof planes...'));

  try {
    await designService.projects.replaceRoofPlanes(state.application.openContext.projectGuid, state.layout.roofPlanes);
    return true;
  } catch (error) {
    console.error(error);
    dispatch(enqueueError(ERROR_SAVING_MESSAGE));
    return false;
  } finally {
    dispatch(removeSpinnerMessage(spinnerId));
  }
};

export const launchFasciaHeightsTool = () => setMatchFasciaHeightsPanelOpen();
